import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { StorageService } from './storage.service';
import { isJwtExpired } from '../utils/jwt.util';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(StorageService);
  private readonly router = inject(Router);

  private handlingSessionExpiry = false;

  readonly user = signal<Record<string, unknown> | null>(
    this.storage.getUser(),
  );

  login(email: string, password: string) {
    return this.http
      .post<{ data?: { accessToken?: string; user?: Record<string, unknown> } }>(
        `${environment.apiUrl}/v1/super-admin/auth/login`,
        { email, password },
      )
      .pipe(
        tap((res) => {
          const token = res?.data?.accessToken;
          const user = res?.data?.user;
          if (token) {
            this.storage.setAccessToken(token);
          }
          if (user) {
            this.storage.setUser(user);
            this.user.set(user);
          }
        }),
      );
  }

  forgotPassword(email: string) {
    return this.http.post<{
      data?: { message?: string };
      message?: string;
    }>(`${environment.apiUrl}/v1/super-admin/auth/forgot-password`, { email });
  }

  validateResetToken(token: string) {
    return this.http.post<{
      data?: { valid?: boolean; expired?: boolean };
    }>(`${environment.apiUrl}/v1/super-admin/auth/validate-reset-token`, { token });
  }

  resetPassword(token: string, newPassword: string) {
    return this.http.post(`${environment.apiUrl}/v1/super-admin/auth/reset-password`, {
      token,
      newPassword,
    });
  }

  logout(): void {
    this.storage.clear();
    this.user.set(null);
    void this.router.navigate(['/auth/login']);
  }

  /** Clears session and redirects when the access token is invalid or expired. */
  handleSessionExpired(): void {
    if (this.handlingSessionExpiry) {
      return;
    }

    this.handlingSessionExpiry = true;
    this.storage.clear();
    this.user.set(null);

    void this.router
      .navigate(['/auth/login'], {
        queryParams: { sessionExpired: '1' },
        replaceUrl: true,
      })
      .finally(() => {
        this.handlingSessionExpiry = false;
      });
  }

  isAuthenticated(): boolean {
    const token = this.storage.getAccessToken();
    if (!token) {
      return false;
    }

    if (isJwtExpired(token)) {
      this.storage.clear();
      this.user.set(null);
      return false;
    }

    return true;
  }
}
